import { ScreeningDtoStatus } from '@/api/generated/model';
import { findIcon } from '@/components/icons/icon-catch/icon-catch';
import { useMe } from '@/context/me/me.context';
import { useGetRisk } from '@/queries/risk.queries';
import { useGetCompletedTestingPlans, useGetTestingPlan } from '@/queries/testing-plan.queries';
import { sortCancerRisks, sortTestingRecommendations } from '@/utils';
import { subYears } from 'date-fns';

export default function useTestingPlanController() {
  // Risk Data
  const { data: riskData } = useGetRisk();
  const unorderedRisks = riskData?.risk.risks ?? [];
  const orderedRisks = unorderedRisks.sort(sortCancerRisks);

  const { data: userData } = useMe();
  // We don't expose the start date via the API, so we'll just assume it's 365 days before the end date
  const planEndDate = userData?.membership.renewalDate ?? new Date();
  const planStartDate = subYears(planEndDate, 1).toISOString();

  // Testing Plan Data
  const { data } = useGetTestingPlan();

  const { recommendations, otherConsiderations } = data ?? { recommendations: [], otherConsiderations: [] };

  const orderedRecommendations = sortTestingRecommendations(orderedRisks, recommendations);
  const processedRecommendations = orderedRecommendations.map((screeningType) => ({
    ...screeningType,
    iconId: findIcon(screeningType.icon),
  }));

  const orderedOtherConsiderations = sortTestingRecommendations(orderedRisks, otherConsiderations);
  const processedOtherConsiderations = orderedOtherConsiderations.map((screeningType) => ({
    ...screeningType,
    iconId: findIcon(screeningType.icon),
  }));

  const filteredRecommendations = processedRecommendations.filter(
    (recommendation) => recommendation.options[0].status === ScreeningDtoStatus.surfaced
  );
  const filteredOtherConsiderations = processedOtherConsiderations.filter(
    (recommendation) => recommendation.options[0].status === ScreeningDtoStatus.surfaced
  );

  const toDoCount = filteredRecommendations.length ?? 0;
  const { data: history } = useGetCompletedTestingPlans();
  const completedCount = history?.length ?? 0;

  return {
    recommendations: filteredRecommendations,
    otherConsiderations: filteredOtherConsiderations,
    planStartDate,
    planEndDate,
    toDoCount,
    completedCount,
  };
}
